/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { X, Star, Shield, Users, Trophy, Lock, Filter } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useTeamDetail } from "@/hooks/api/useTeams";
import { useTeamPlayers } from "@/hooks/api/useLeagues";
import { useGameweeks } from "@/hooks/api/useGameweeks";
import type { Gameweek } from "@/types/api.types";

const POSITION_COLORS: Record<string, string> = {
  GK: "bg-yellow-100 text-yellow-800",
  DEF: "bg-blue-100 text-blue-800",
  MID: "bg-green-100 text-green-800",
  FWD: "bg-red-100 text-red-800",
};

const POSITION_ORDER = ["GK", "DEF", "MID", "FWD"];

interface Props {
  teamId: number;
  onClose: () => void;
}

export default function TeamDetailDrawer({ teamId, onClose }: Props) {
  const [selectedGameweekId, setSelectedGameweekId] = useState<number | null>(
    null,
  );

  const { data: team, isLoading: teamLoading } = useTeamDetail(teamId);
  const { data: gameweeks } = useGameweeks();

  // When a gameweek is selected, fetch team players for that gameweek
  // We need the team's internal teamId for the league team-players endpoint
  const {
    data: gwTeam,
    isLoading: gwLoading,
  } = useTeamPlayers(teamId, selectedGameweekId ?? undefined);

  const isLoading = selectedGameweekId ? gwLoading : teamLoading;

  // Build player list based on current view
  const players: any[] = selectedGameweekId
    ? gwTeam?.players ?? []
    : team?.players ?? [];

  const totalPoints = selectedGameweekId
    ? gwTeam?.totalPoints ?? 0
    : team?.totalPoints ?? 0;

  const captainId = selectedGameweekId
    ? gwTeam?.captainId
    : team?.captainId;

  // Group players by position
  const grouped = players.reduce(
    (acc: Record<string, any[]>, p: any) => {
      const pos = p.position || p.positionInTeam;
      if (!acc[pos]) acc[pos] = [];
      acc[pos].push(p);
      return acc;
    },
    {},
  );

  // Sort gameweeks: non-UPCOMING first, then by phase/round
  const sortedGameweeks = [...(gameweeks ?? [])].filter(
    (gw: Gameweek) => gw.status !== "UPCOMING",
  );

  const selectedGw = sortedGameweeks.find(
    (gw: Gameweek) => gw.id === selectedGameweekId,
  );

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-[480px] bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Shield className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {teamLoading ? "Loading..." : team?.name}
              </h2>
              {team && (
                <p className="text-sm text-gray-500">
                  {team.formation} · {totalPoints} pts
                  {selectedGw && (
                    <span className="text-blue-500 ml-1">
                      ({selectedGw.name})
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Gameweek Filter */}
        <div className="px-6 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-2 mb-2">
            <Filter className="h-3.5 w-3.5 text-gray-400" />
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Filter by Matchday
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setSelectedGameweekId(null)}
              className={`px-3 py-1.5 text-xs rounded-full font-medium transition ${
                !selectedGameweekId
                  ? "bg-green-600 text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
              }`}
            >
              All Time
            </button>
            {sortedGameweeks.map((gw: Gameweek) => (
              <button
                key={gw.id}
                onClick={() => setSelectedGameweekId(gw.id)}
                className={`px-3 py-1.5 text-xs rounded-full font-medium transition ${
                  selectedGameweekId === gw.id
                    ? "bg-green-600 text-white"
                    : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
                }`}
              >
                {gw.name}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
          </div>
        ) : players.length > 0 ? (
          <div className="flex-1 overflow-y-auto">
            {/* Team Stats */}
            <div className="grid grid-cols-3 gap-4 px-6 py-4 bg-gray-50 border-b">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Trophy className="h-4 w-4 text-yellow-500" />
                  <span className="text-xl font-bold text-gray-900">
                    {totalPoints}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  {selectedGameweekId ? "GW Points" : "Total Points"}
                </p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Users className="h-4 w-4 text-blue-500" />
                  <span className="text-xl font-bold text-gray-900">
                    {players.length}
                  </span>
                </div>
                <p className="text-xs text-gray-500">Players</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Lock className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-bold text-gray-900">
                    {team?.isLocked ? "Locked" : "Active"}
                  </span>
                </div>
                <p className="text-xs text-gray-500">Status</p>
              </div>
            </div>

            {/* Owner Info */}
            <div className="px-6 py-4 border-b">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Owner
              </h3>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center font-bold text-green-700">
                  {team?.user?.name?.charAt(0) ?? "?"}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    {team?.user?.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    @{team?.user?.username || "\u2014"} · {team?.user?.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Players by Position */}
            <div className="px-6 py-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
                Squad {selectedGw ? `\u2014 ${selectedGw.name}` : ""}
              </h3>

              {POSITION_ORDER.map((pos) => {
                const posPlayers = grouped?.[pos];
                if (!posPlayers?.length) return null;

                return (
                  <div key={pos} className="mb-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={POSITION_COLORS[pos]}>
                        {pos === "GK"
                          ? "Goalkeeper"
                          : pos === "DEF"
                            ? "Defenders"
                            : pos === "MID"
                              ? "Midfielders"
                              : "Forwards"}
                      </Badge>
                      <span className="text-xs text-gray-400">
                        {posPlayers.length} players
                      </span>
                    </div>

                    <div className="space-y-2">
                      {posPlayers.map((player: any) => {
                        const isCaptain = selectedGameweekId
                          ? player.isCaptain
                          : captainId === player.id;
                        const pts = player.points ?? 0;

                        return (
                          <div
                            key={player.id || player.playerId}
                            className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${
                              isCaptain
                                ? "bg-yellow-50 border-yellow-200"
                                : "bg-gray-50 border-gray-100"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-gray-400 w-5 text-center">
                                {player.jerseyNumber
                                  ? `#${player.jerseyNumber}`
                                  : "\u2014"}
                              </span>
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <span className="font-medium text-sm text-gray-900">
                                    {player.name}
                                  </span>
                                  {isCaptain && (
                                    <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                                  )}
                                  {!player.isActive && (
                                    <Badge className="bg-red-100 text-red-600 text-[10px] px-1 py-0">
                                      Out
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-gray-400">
                                  {player.nation?.name ?? player.nation ?? "\u2014"}
                                </p>
                              </div>
                            </div>

                            <div className="text-right">
                              <p
                                className={`font-bold text-sm ${isCaptain ? "text-yellow-600" : "text-green-600"}`}
                              >
                                {pts}
                              </p>
                              {isCaptain && (
                                <p className="text-[10px] text-yellow-500">
                                  captain
                                </p>
                              )}
                              {/* Show stats breakdown for gameweek view */}
                              {selectedGameweekId && player.stats && (
                                <div className="flex gap-1 mt-0.5 justify-end">
                                  {player.stats.goals > 0 && (
                                    <span className="text-[9px] bg-green-100 text-green-700 px-1 rounded">
                                      {player.stats.goals}G
                                    </span>
                                  )}
                                  {player.stats.assists > 0 && (
                                    <span className="text-[9px] bg-blue-100 text-blue-700 px-1 rounded">
                                      {player.stats.assists}A
                                    </span>
                                  )}
                                  {player.stats.cleanSheets > 0 && (
                                    <span className="text-[9px] bg-purple-100 text-purple-700 px-1 rounded">
                                      CS
                                    </span>
                                  )}
                                  {player.stats.yellowCards > 0 && (
                                    <span className="text-[9px] bg-yellow-100 text-yellow-700 px-1 rounded">
                                      YC
                                    </span>
                                  )}
                                  {player.stats.redCards > 0 && (
                                    <span className="text-[9px] bg-red-100 text-red-700 px-1 rounded">
                                      RC
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            {selectedGameweekId
              ? "No team data for this matchday"
              : "Team not found"}
          </div>
        )}
      </div>
    </>
  );
}
